/**
 * <b>우리가 css사용하는 단위들의 집합입니다.</b>
 */
let units = ['%', 'px', 'em', 'rem', 'vh', 'vw', 'vmin', 'vmax', 'ex', 'ch'];

/**
 * <b>우리가 검사할 연산자 단위입니다.</b>
 */
let ops = ['+', '-', '*', '/'];

/**
 * <b>특정 단위를 만드는데 특화된 메소드를 모아둔 객체입니다.</b>
 * <pre>
 * vue에서는 css에 사용하는 여러 단위가 있는데 사용하기 불편합니다.
 * 이를 쉽게 사용하기 위해서 만들어둔 단위 조정 메소드입니다.
 * </pre>
 */
class Unit {

    /**
     * <b>올바른 단위인지 확인합니다.</b>
     * <pre>
     * 우리가 사용하는 단위가 항상 올바른 단위라는 보장이 없습니다.
     * units배열과 비교하여서 우리가 사용하는 단위가 맞는지 확인합니다.
     * </pre>
     * @param {Any} value - 헤딩 딘위를 받습니다.
     * @return {Boolean} valid - 해당 단위가 옳은지 그른지 리턴합니다.
     */
    checkValueUnitValid(value) {
        if (typeof value === 'number' && !isNaN(value)) {
            return true;
        }
        value = new String(value);
        for (var i = 0; i < value.length; i++) {
            if (isNaN(value[i]) && value[i] !== '.') {
                break;
            }
        }
        if (i == value.length) {
            //This is number;
            return true;
        } else {
            for (let unit of units) {
                if (unit == value.substr(i, value.length)) {
                    return true;
                }
            }
        }
        return false;
    }

    /**
     * <b>해당 값들에 단위를 붙혀줍니다.</b>
     * <pre>
     * 단위가 배열로 넘어왔을떄 그 배열전체를 확인해서 단위를 붙힙니다. 값이 올바르지 않다면 0이 리턴됩ㄴ디ㅏ.
     * </pre>
     * @param {Array} arr - 값들의 배열이 넘겨집니다.
     * @param {String} unit - 단위를 반환합니다.
     * @return {Array} arr - 값들에 단위를 붙혀서 다시 리턴합니다.
     */
    makeUnits(arr, unit = 'px') {
        for (let i = 0; i < arr.length; i++) {
            if (typeof (arr[i]) !== 'string' && this.checkValueUnitValid(arr[i])) {
                arr[i] += unit;
            } else if (typeof (arr[i]) === 'string' && !this.checkValueUnitValid(arr[i])) {
                arr[i] = `0${unit}`;
            } else if (typeof (arr[i]) !== 'string') {
                arr[i] = `0${unit}`;
            }
        }
        return arr;
    }

    /**
     * <b>해당 값에 단위를 붙혀줍니다. 기본 단위는 px입니다.</b>
     * <pre>
     * 값은 단위가 붙은 값이 넘어온다면 아무 작업을 하지 않습니다.
     * 값이 올바르지 않거나 단위가 올바르지 않으면 적절한 조취를 치해서 넘깁니다.
     * 되도록 값이나 단위를 잘못적는 일은 없도록 하시길 바랍니다.
     * </pre>
     * @param {Any} data - 값이 올바른지 확인합니다.
     * @param {String} unit - 단위를 적습니다. 
     * @return {String} data - 값에 단위를 붙혀서 넘겨줍니다.
     */
    makeUnit(data, unit = 'px') {
        for (var i = 0; i < units.length; i++) {
            if (units[i] === unit) {
                break;
            }
        }
        if (i === units.length && typeof data === 'number') {
            return data + 'px';
        }
        if (typeof (data) !== 'string' && this.checkValueUnitValid(data)) {
            data += unit;
        } else if (typeof (data) === 'string' && !this.checkValueUnitValid(data)) {
            data = `0${unit}`;
        } else if (typeof (data) !== 'string') {
            data = `0${unit}`;
        }
        return data;
    }

    /**
     * <b>퍼센트값을 소수로 변환합니다.</b>
     * <pre>
     * 퍼센트 값을 소수로 변환합니다. %값이 아니라면 null을 반환합니다.
     * </pre>
     * @param {String} value - %값이 넘어옵니다. 
     * @return {Number} valid - null혹은 float을 리턴합니다.
     */
    percentToFloat(value) {
        return typeof value === 'string' && value[value.length - 1] === '%' && this.checkValueUnitValid(value) ? parseFloat(value) / 100.0 : null;
    }

    /**
     * <b>해당 값이 퍼센트값인지 확인합니다.</b>
     * <pre>
     * 해당 값이 퍼센트인지 확인하며 맞다면 true, 아니면 false를 리턴합니다.
     * 단 %기호가 반드시 붙어야합니다.
     * </pre>
     * @param {String} value - 퍼센트기호가 붙은 값이 넘어옵니다.
     * @return {Boolean} valid - 값이 옳은지 그른지를 리턴합니다.
     */
    checkPercent(value) {
        if (typeof value !== 'string') {
            return false;
        }
        for (var i = 0; i < value.length - 1; i++) {
            if (isNaN(value[i]) && value[i] !== '.') {
                return false;
            }
        }
        return value[value.length - 1] === '%';
    }

    /**
     * <b>두 배열의 값을 비교해서 값들이 같은지를 비교합니다.</b>
     * <pre>
     * 두 배열이 동등한 값을 가졌는지 비교합니다.
     * 두 배열 자체가 동등한지를 비교하는건 아닙니다.
     * </pre>
     * @param {Array} arr1 - 비교할 객체1
     * @param {Arrau} arr2 - 비교할 객체2
     * @return {Boolean} valid - 두 배열의 동등성을 반환합니다.
     */
    compareArray(arr1, arr2) {
        if (arr1 === null && arr2 === null) {
            return true;
        } else if (arr1 === undefined && arr2 === undefined) {
            return true;
        } else if ((arr1 && arr2) && arr1.length === arr2.length) {
            for (var i = 0; i < arr1.length; i++) {
                if (arr1[i] != arr2[i] && typeof arr1[i] === typeof arr2[i]) {
                    break;
                }
            }
            if (i === arr1.length) {
                return true;
            }
        }
        return false;
    }

    /**
     * <b>bolder를 반환힙니다.</b>
     * <pre>
     * bolder자체는 변수로 다루기 까다롭습니다.
     * 따라서이를 제작해주는 메소드이니다.
     * unit,type,thickness순으로 생략가능합니다.
     * </pre>
     * @param {String} color - 색상을 정합니다.
     * @param {Stromg} thickness - 두께를 정합니다.
     * @param {String} type - 타입을 정합니다.
     * @param {String} unit - 단위를 정합니다.
     * @return {String} bolder - 두께선을 반환합니다.
     */
    makeBorder(color = 'black', thickness = '1', type = 'solid', unit = 'px') {
        return `${thickness}${unit} ${type} ${color}`;
    }

    /**
     * <b>해당 단위가 옳은 단위인지 검색한다.</b>
     * <pre>
     * 해당 단위가 옳은 단위인지 틀린 단위인지를 검사합니다.
     * 다만 모든 단위가 들어있는건 아니니 반드시 ops 배열을 확인하시기 바랍니다.
     * </pre>
     * @param {String} value - 체크할 단위를 적으시면 됩니다. 
     * @return {Boolean} valid - 단위가 옳은 단위면 true 아니면 false를 리턴합니다.
     */
    checkOpValid(value) {
        for (let op of ops) {
            if (value == op) {
                return true;
            }
        }
        return false;
    }

    /**
     * <b>calc값에 변수를 넣습니다.<b>
     * <pre>
     * clac(A op B)형태를 자주 만드는데 변수로 만들기 까다롭습니다.
     * 따라서 이를 쉽게 만들어주는 메소드 입니다.
     * </pre>
     * @param {String} a - 단위1 입니다. 
     * @param {String} op - 연산자 입니다.
     * @param {String} b - 단위2 입니다.
     * @return {String} unit - 최종 결과가 리턴됩니다. 그중에 옳지 못한 값이 있을경우 0이 리턴됩니다.
     */
    calcAOB(a, op, b) {
        if (this.checkOpValid(op) && this.checkValueUnitValid(a) && this.checkValueUnitValid(b)) {
            return `calc( ${a} ${op} ${b} )`;
        }
        return '0';
    }

    /**
     * <b>rotate를 만듭니다.</b>
     * <pre>
     * rotate를 만듭니다. 단위는 deg입니다.
     * </pre>
     * @param {String} data - rotate에 넣을 값입니다.
     * @return {String} rotate - 반환된 rotate 값입니다.
     */
    makeRotate(data) {
        return `rotate(${data}deg)`;
    }

    /**
     * <b>문자열 뒤에 important를 붙힙니다.</b>
     * @param {String} str - important를 붙힐 문자열입니다.
     * @return {String} str - important를 붙은 값을 반환합니다. 
     */
    addImportant(str) {
        return `${str} !important`;
    }

    /**
     * <b>날짜 문자열을 String으로 바꿉니다</b>
     * <pre>
     * String이 YY:MM::DD hh:mm:ss 형태일 때만 가능합니다.
     * Safari, iPone, iPad에서 기존 new Date로는 생성이 안됩니다. 나머지 브라우저 됩니다.
     * </pre>
     * @param {String} str - Date로 반환시킬 문자열 입니다.
     * @return {Date} date - 변환된 Date를 반환합니다.
     */
    makeDate(str) {
        //TODO 날짜관련 문제가 생긴다면 이 부분을 보세요.
        let year = str.substring(0, 4);
        let month = parseInt(str.substring(5, 7)) - 1;
        let day = str.substring(8, 10);
        let hour = str.substring(11, 13);
        let minute = str.substring(14, 16);
        let second = str.substring(17, 19);
        let date = new Date(year, month, day, hour, minute, second);
        return date;
    }
}

export default new Unit();